import { Product } from '../../../../../../../../../shared/modules/evc-api/models/product';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ChargePointLocation, ChargePointType } from '../../../../../../../stores/small-biz-store.model';
import { ProductsStoreService } from '../../../../../../../stores/products-store.service';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { ProductSetupMapperService } from '../../../../../../../stores/product-setup.mapper.service';
import { isOneOf } from '../../../../../../helpers/is-one-of-enum';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
  selector: 'evsb-charging-station-placement',
  templateUrl: './charging-station-placement.component.html',
  styleUrls: ['./charging-station-placement.component.scss'],
})
export class ChargingStationPlacementComponent implements OnChanges {
  @Input()
  public desiredSpeed: ChargePointType;
  @Input()
  public placement: ChargePointLocation;
  @Output()
  public placementChange = new EventEmitter<ChargePointLocation>();

  public desiredSpeed$ = new BehaviorSubject<ChargePointType>(null);

  public get chosenPlacement(): string {
    return this.mapper.locationTypeToProductCode(this.placement);
  }

  public chargingStationPlacements$ = this.desiredSpeed$.pipe(
    distinctUntilChanged(),
    filter((type) => isOneOf(ChargePointType, type)),
    switchMap((type) => this.products.findByChargePointType(type)),
    switchMap((product) => this.products.getPlacementsFor(product))
  );

  constructor(private products: ProductsStoreService, private mapper: ProductSetupMapperService) {}

  public ngOnChanges(changes: SimpleChanges) {
    this.desiredSpeed$.next(this.desiredSpeed);
  }

  public updatePlacement(placementCode: string) {
    this.placementChange.emit(this.mapper.productCodeToChargePointLocation(placementCode));
  }

  public getLabels(product: Product) {
    const location = this.mapper.productCodeToChargePointLocation(product.code);
    return chargingStationPlacementLabels[location];
  }
}

const chargingStationPlacementLabels: Record<
  ChargePointLocation,
  {
    title: string;
    img: string;
    list1: string;
    list2: string;
    list3: string;
    details1: {
      title: string;
      text: string;
    };
    details2: {
      title: string;
      text: string;
    };
    details3: {
      title: string;
      text: string;
    };
  }
> = {
  [ChargePointLocation.wallInside30]: {
    title: marker('modules.evsb.chargingStation.input.location.wallInside30.title'),
    img: './assets/evsb/img/charging-station/inside-wall.svg',
    list1: marker('modules.evsb.chargingStation.input.location.wallInside30.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.wallInside30.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.wallInside30.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside30.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside30.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside30.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside30.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside30.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside30.details3.text'),
    },
  },
  [ChargePointLocation.wallInside50]: {
    title: marker('modules.evsb.chargingStation.input.location.wallInside50.title'),
    img: './assets/evsb/img/charging-station/inside-wall.svg',
    list1: marker('modules.evsb.chargingStation.input.location.wallInside50.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.wallInside50.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.wallInside50.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside50.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside50.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside50.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside50.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.wallInside50.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallInside50.details3.text'),
    },
  },
  [ChargePointLocation.wallOutside30]: {
    title: marker('modules.evsb.chargingStation.input.location.wallOutside30.title'),
    img: './assets/evsb/img/charging-station/outside-wall.svg',
    list1: marker('modules.evsb.chargingStation.input.location.wallOutside30.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.wallOutside30.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.wallOutside30.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside30.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside30.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside30.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside30.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside30.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside30.details3.text'),
    },
  },
  [ChargePointLocation.wallOutside50]: {
    title: marker('modules.evsb.chargingStation.input.location.wallOutside50.title'),
    img: './assets/evsb/img/charging-station/outside-wall.svg',
    list1: marker('modules.evsb.chargingStation.input.location.wallOutside50.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.wallOutside50.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.wallOutside50.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside50.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside50.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside50.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside50.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.wallOutside50.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.wallOutside50.details3.text'),
    },
  },
  [ChargePointLocation.poleOutside30]: {
    title: marker('modules.evsb.chargingStation.input.location.poleOutside30.title'),
    img: './assets/evsb/img/charging-station/outside-pole.svg',
    list1: marker('modules.evsb.chargingStation.input.location.poleOutside30.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.poleOutside30.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.poleOutside30.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside30.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside30.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside30.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside30.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside30.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside30.details3.text'),
    },
  },
  [ChargePointLocation.poleOutside50]: {
    title: marker('modules.evsb.chargingStation.input.location.poleOutside50.title'),
    img: './assets/evsb/img/charging-station/outside-pole.svg',
    list1: marker('modules.evsb.chargingStation.input.location.poleOutside50.list1'),
    list2: marker('modules.evsb.chargingStation.input.location.poleOutside50.list2'),
    list3: marker('modules.evsb.chargingStation.input.location.poleOutside50.list3'),
    details1: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside50.details1.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside50.details1.text'),
    },
    details2: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside50.details2.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside50.details2.text'),
    },
    details3: {
      title: marker('modules.evsb.chargingStation.input.location.poleOutside50.details3.title'),
      text: marker('modules.evsb.chargingStation.input.location.poleOutside50.details3.text'),
    },
  },
};
